export const timeUnits = ['seconds', 'minutes', 'hours', 'days'] as const

export type TimeUnit = (typeof timeUnits)[number]

export type TimeUnitWithAmount = { unit: TimeUnit; amount: number }

export const SECONDS_PER_DAY = 24 * 60 * 60
export const SECONDS_PER_HOUR = 60 * 60
export const SECONDS_PER_MINUTE = 60

const dividers: Record<TimeUnit, number> = {
  seconds: 1,
  minutes: SECONDS_PER_MINUTE,
  hours: SECONDS_PER_HOUR,
  days: SECONDS_PER_DAY,
}

export const timeUnitToSeconds = (amount: number, unit: TimeUnit): number => {
  const multipliers: Record<TimeUnit, number> = {
    seconds: 1,
    minutes: SECONDS_PER_MINUTE,
    hours: SECONDS_PER_HOUR,
    days: SECONDS_PER_DAY,
  }

  return amount * multipliers[unit]
}

export const unitForSeconds = (seconds: number): TimeUnit => {
  let result: TimeUnit = 'seconds'
  timeUnits.forEach((item) => {
    if (seconds % dividers[item] === 0) {
      result = item
    }
  })

  return result
}

export const unitAndAmountForSeconds = (seconds: number, minimalReturnUnit?: TimeUnit): TimeUnitWithAmount => {
  const unit = unitForSeconds(seconds)

  if (!minimalReturnUnit) {
    return { unit, amount: amountForUnit(seconds, unit) }
  }

  const maxUnit = ((compareResult) => {
    if (compareResult > 0) {
      return unit
    }
    return minimalReturnUnit
  })(compareUnits(unit, minimalReturnUnit))

  return { unit: maxUnit, amount: amountForUnit(seconds, maxUnit) }
}

export const secondsToHumanFormatShort = (secondsTotal: number): { minutes: number; seconds: number } => {
  if (secondsTotal <= 0) {
    return { minutes: 0, seconds: 0 }
  }

  const minutes = Math.floor(secondsTotal / SECONDS_PER_MINUTE)
  const seconds = secondsTotal - minutes * SECONDS_PER_MINUTE

  return { minutes, seconds }
}

const compareUnits = (a: TimeUnit, b: TimeUnit): number => {
  const unitWeights: Record<TimeUnit, number> = {
    days: 3,
    hours: 2,
    minutes: 1,
    seconds: 0,
  }

  return unitWeights[a] - unitWeights[b]
}

const amountForUnit = (seconds: number, timeUnit: TimeUnit): number => Math.round(seconds / dividers[timeUnit])
